/**
 * Per-day attempt state, persisted to localStorage and keyed by the
 * puzzle id (which is `YYYY-MM-DD` in v1, but stays opaque here in case
 * the puzzle source ever changes).
 *
 * BigInts can't round-trip through JSON, so the canonical blend is
 * stored as a decimal string and inflated to `bigint` on read.
 */

/// The number of top scores kept per puzzle. Submissions beyond this are
/// silently dropped if they don't beat the current 10th-place entry.
export const TOP_SCORE_LIMIT = 10;

const STORAGE_KEY_PREFIX = "cogquest:daily:";
const DRAFT_KEY_PREFIX = "cogquest:draft:";

// Soft cap on what we'll persist as a draft. The encoder maxes out at
// 40 tokens, so this is generous — protects localStorage from
// pathologically long pastes without rejecting any real sentence.
const MAX_DRAFT_LENGTH = 4096;

/**
 * A persisted attempt. By contract, only gate-passing submissions are
 * ever stored — the UI doesn't burn attempt slots on failed scores.
 * Old data that pre-dates this contract is filtered at load time
 * (failing entries are dropped, not migrated).
 */
export interface Attempt {
  readonly sentence: string;
  readonly canonicalBlend: bigint;
  readonly submittedAt: number;
}

export interface DailyState {
  readonly puzzleId: string;
  readonly attempts: readonly Attempt[];
}

function storageKey(puzzleId: string): string {
  return STORAGE_KEY_PREFIX + puzzleId;
}

/// Wire shape on disk. `gatesPass` is optional for forward-compat with
/// the post-refactor schema, but if present and `false`, we drop the
/// entry on load (it doesn't satisfy the only-passing-is-stored
/// contract).
interface SerializedAttempt {
  readonly sentence: string;
  readonly gatesPass?: boolean;
  readonly canonicalBlend: string;
  readonly submittedAt: number;
}

function isSerializedAttempt(value: unknown): value is SerializedAttempt {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.sentence === "string" &&
    (v.gatesPass === undefined || typeof v.gatesPass === "boolean") &&
    typeof v.canonicalBlend === "string" &&
    typeof v.submittedAt === "number"
  );
}

function parseAttempt(value: unknown): Attempt | undefined {
  if (!isSerializedAttempt(value)) return undefined;
  // Pre-refactor entries may have `gatesPass: false`; those aren't
  // valid attempts under the new contract, so drop them.
  if (value.gatesPass === false) return undefined;
  try {
    return {
      sentence: value.sentence,
      canonicalBlend: BigInt(value.canonicalBlend),
      submittedAt: value.submittedAt,
    };
  } catch {
    return undefined;
  }
}

function serializeAttempt(attempt: Attempt): SerializedAttempt {
  return {
    sentence: attempt.sentence,
    canonicalBlend: attempt.canonicalBlend.toString(),
    submittedAt: attempt.submittedAt,
  };
}

/**
 * Returns the persisted attempts for a puzzle, or an empty list if
 * nothing has been stored yet or the stored payload is corrupt.
 */
export function loadDailyState(puzzleId: string): DailyState {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(puzzleId));
  } catch {
    // localStorage can throw in private-browsing edge cases.
    return { puzzleId, attempts: [] };
  }
  if (raw === null) return { puzzleId, attempts: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { puzzleId, attempts: [] };
  }
  if (typeof parsed !== "object" || parsed === null) {
    return { puzzleId, attempts: [] };
  }
  const list = (parsed as { attempts?: unknown }).attempts;
  if (!Array.isArray(list)) return { puzzleId, attempts: [] };
  const attempts: Attempt[] = [];
  for (const entry of list) {
    const parsedAttempt = parseAttempt(entry);
    if (parsedAttempt !== undefined) attempts.push(parsedAttempt);
  }
  // Old data is insertion-ordered; normalize to the post-refactor invariant
  // (top scores desc, capped at TOP_SCORE_LIMIT). Same shape we write.
  return { puzzleId, attempts: topByBlend(attempts) };
}

function compareByBlendDesc(a: Attempt, b: Attempt): number {
  if (a.canonicalBlend === b.canonicalBlend) return 0;
  return a.canonicalBlend > b.canonicalBlend ? -1 : 1;
}

function topByBlend(attempts: readonly Attempt[]): Attempt[] {
  return [...attempts].sort(compareByBlendDesc).slice(0, TOP_SCORE_LIMIT);
}

/**
 * Records a new gate-passing attempt and returns the resulting state.
 *
 * The contract is "track the top 10 by canonical blend, unbounded
 * submissions": we drop any existing entry whose sentence matches
 * (resubmitting the same text shouldn't multiply-count), insert the
 * new attempt, sort by canonical blend descending, and trim to
 * `TOP_SCORE_LIMIT`. Submissions that don't beat the 10th-place blend
 * are silently dropped from storage; the caller can still surface the
 * "+score" feedback on its own.
 */
export function appendAttempt(puzzleId: string, attempt: Attempt): DailyState {
  const current = loadDailyState(puzzleId);
  const dedup = current.attempts.filter((a) => a.sentence !== attempt.sentence);
  const next: DailyState = { puzzleId, attempts: topByBlend([...dedup, attempt]) };
  try {
    localStorage.setItem(
      storageKey(puzzleId),
      JSON.stringify({ attempts: next.attempts.map(serializeAttempt) }),
    );
  } catch {
    // No-op: a full or unavailable localStorage shouldn't break play.
  }
  return next;
}

/**
 * Returns the user's in-progress sentence for a puzzle, or `""` if no
 * draft is stored. Used to repopulate the textarea after a refresh so a
 * stray reload doesn't nuke typing.
 */
export function loadDraft(puzzleId: string): string {
  try {
    return localStorage.getItem(DRAFT_KEY_PREFIX + puzzleId) ?? "";
  } catch {
    return "";
  }
}

/// Persists the in-progress sentence. Empty strings are cleared rather
/// than stored, so the key is absent unless there's something to save.
export function saveDraft(puzzleId: string, sentence: string): void {
  try {
    if (sentence.length === 0) {
      localStorage.removeItem(DRAFT_KEY_PREFIX + puzzleId);
      return;
    }
    const truncated =
      sentence.length > MAX_DRAFT_LENGTH ? sentence.slice(0, MAX_DRAFT_LENGTH) : sentence;
    localStorage.setItem(DRAFT_KEY_PREFIX + puzzleId, truncated);
  } catch {
    // Quota or unavailable storage: drafts are best-effort.
  }
}

/// Removes the draft for a puzzle. Call after a successful submit so
/// the textarea doesn't repopulate with the just-scored sentence.
export function clearDraft(puzzleId: string): void {
  try {
    localStorage.removeItem(DRAFT_KEY_PREFIX + puzzleId);
  } catch {
    // best-effort
  }
}

/**
 * The highest canonical blend across stored attempts. **Higher** wins
 * per the cogcoin protocol — see `scoring_bundle/scoring_module.md`:
 * "only the one with the highest blend score is kept." All stored
 * attempts are gate-passing by construction, so no extra filter.
 * Returns `undefined` for an empty list.
 */
export function bestCanonicalBlend(attempts: readonly Attempt[]): bigint | undefined {
  let best: bigint | undefined;
  for (const a of attempts) {
    if (best === undefined || a.canonicalBlend > best) best = a.canonicalBlend;
  }
  return best;
}
